import { useState, useRef, useCallback, useEffect } from 'react'

interface VoiceInputProps {
  onTranscript: (text: string) => void
  onSOAPUpdate?: (soap: { s: string; o: string; a: string; p: string }) => void
  placeholder?: string
  geminiApiKey?: string
  autoStart?: boolean
  onAutoStartChange?: (enabled: boolean) => void
}

const DEEPGRAM_API_KEY = '38a52e742628c2b53d5d0d72cbb75399297fc6fd'
const DEFAULT_GEMINI_API_KEY = 'AIzaSyDqjjjVZKZyoDCjfwLRaHeT0ypfuMzOYKs'
const AUTO_START_KEY = 'fmt-japan-voice-auto-start'

export default function VoiceInput({ 
  onTranscript, 
  onSOAPUpdate, 
  placeholder, 
  geminiApiKey,
  autoStart = false,
  onAutoStartChange 
}: VoiceInputProps) {
  const [isRecording, setIsRecording] = useState(false)
  const [transcript, setTranscript] = useState('')
  const [liveTranscript, setLiveTranscript] = useState('')
  const [isProcessing, setIsProcessing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [soapPreview, setSoapPreview] = useState({ s: '', o: '', a: '', p: '' })
  const [apiKeyInput, setApiKeyInput] = useState(geminiApiKey || DEFAULT_GEMINI_API_KEY)
  const [showApiKeyInput, setShowApiKeyInput] = useState(false)
  const [autoStartEnabled, setAutoStartEnabled] = useState(() => {
    const saved = localStorage.getItem(AUTO_START_KEY)
    return saved ? JSON.parse(saved) : false
  })
  
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const socketRef = useRef<WebSocket | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const confirmedTextRef = useRef<string>('') // 確定済みテキスト
  const lastProcessedRef = useRef<string>('')
  const processingIntervalRef = useRef<number | null>(null)
  const hasAutoStarted = useRef(false)

  // 自動開始設定の保存
  const handleAutoStartChange = useCallback((enabled: boolean) => {
    setAutoStartEnabled(enabled)
    localStorage.setItem(AUTO_START_KEY, JSON.stringify(enabled))
    onAutoStartChange?.(enabled)
  }, [onAutoStartChange])

  // Gemini APIを使ってSOAP分類
  const classifyWithGemini = useCallback(async (text: string) => {
    if (!apiKeyInput || !text.trim() || text === lastProcessedRef.current) return
    
    lastProcessedRef.current = text
    setIsProcessing(true)

    try {
      const response = await fetch(
        `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${apiKeyInput}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            contents: [{
              parts: [{
                text: `あなたは医療カルテの記録アシスタントです。以下の診察中の会話テキストをSOAP形式に分類してください。

会話テキスト:
${text}

以下のJSON形式で回答してください。該当する内容がない場合は空文字列にしてください:
{
  "s": "主観的情報（患者の訴え、症状、感じていること）",
  "o": "客観的情報（検査結果、数値、診察所見）",
  "a": "評価（診断、病状評価、改善・悪化の判断）",
  "p": "計画（治療方針、処方、次回予約、指導内容）"
}

JSON形式のみで回答し、他の説明は不要です。`
              }]
            }],
            generationConfig: {
              temperature: 0.1,
              maxOutputTokens: 1024,
            }
          }),
        }
      )

      if (!response.ok) {
        throw new Error('Gemini API error')
      }

      const data = await response.json()
      const content = data.candidates?.[0]?.content?.parts?.[0]?.text

      if (content) {
        const jsonMatch = content.match(/\{[\s\S]*\}/)
        if (jsonMatch) {
          const soap = JSON.parse(jsonMatch[0])
          setSoapPreview({
            s: soap.s || '',
            o: soap.o || '',
            a: soap.a || '',
            p: soap.p || '',
          })
          
          if (onSOAPUpdate) {
            onSOAPUpdate(soap)
          }
        }
      }
    } catch (err) {
      console.error('Gemini classification error:', err)
      const localSoap = classifyLocally(text)
      setSoapPreview(localSoap)
      if (onSOAPUpdate) {
        onSOAPUpdate(localSoap)
      }
    } finally {
      setIsProcessing(false)
    }
  }, [apiKeyInput, onSOAPUpdate])

  // ローカル分類（フォールバック用）
  const classifyLocally = (text: string) => {
    const soap = { s: '', o: '', a: '', p: '' }
    const lines = text.split(/[。、\n]/).filter(l => l.trim())
    
    lines.forEach(line => {
      const lowerLine = line.toLowerCase()
      
      if (lowerLine.includes('痛い') || lowerLine.includes('つらい') || lowerLine.includes('感じ') ||
          lowerLine.includes('思う') || lowerLine.includes('気がする') || lowerLine.includes('調子') ||
          lowerLine.includes('症状') || lowerLine.includes('困って')) {
        soap.s += line + '。'
      } else if (lowerLine.includes('検査') || lowerLine.includes('測定') || lowerLine.includes('数値') ||
               lowerLine.includes('回') || lowerLine.includes('正常') || lowerLine.includes('異常')) {
        soap.o += line + '。'
      } else if (lowerLine.includes('改善') || lowerLine.includes('悪化') || lowerLine.includes('経過') ||
               lowerLine.includes('良好') || lowerLine.includes('診断')) {
        soap.a += line + '。'
      } else if (lowerLine.includes('治療') || lowerLine.includes('処方') || lowerLine.includes('次回') ||
               lowerLine.includes('予定') || lowerLine.includes('指導')) {
        soap.p += line + '。'
      } else if (line.trim()) {
        soap.s += line + '。'
      }
    })

    return soap
  }

  const startRecording = useCallback(async () => {
    try {
      setError(null)
      setTranscript('')
      setLiveTranscript('')
      setSoapPreview({ s: '', o: '', a: '', p: '' })
      confirmedTextRef.current = ''
      lastProcessedRef.current = ''
      
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          channelCount: 1,
          sampleRate: 16000,
        } 
      })
      streamRef.current = stream

      // Deepgram WebSocket接続 (Nova-3モデル)
      const socket = new WebSocket(
        `wss://api.deepgram.com/v1/listen?model=nova-3&language=ja&punctuate=true&smart_format=true&interim_results=true`,
        ['token', DEEPGRAM_API_KEY]
      )
      socketRef.current = socket

      socket.onopen = () => {
        console.log('Deepgram WebSocket connected (Nova-3)')
        
        const mediaRecorder = new MediaRecorder(stream, {
          mimeType: 'audio/webm;codecs=opus',
        })
        mediaRecorderRef.current = mediaRecorder

        mediaRecorder.ondataavailable = (event) => {
          if (event.data.size > 0 && socket.readyState === WebSocket.OPEN) {
            socket.send(event.data)
          }
        }

        mediaRecorder.start(100)
        setIsRecording(true)

        // 定期的にGemini分類を実行（3秒ごと）
        processingIntervalRef.current = window.setInterval(() => {
          if (confirmedTextRef.current.trim()) {
            classifyWithGemini(confirmedTextRef.current)
          }
        }, 3000)
      }

      socket.onmessage = (event) => {
        const data = JSON.parse(event.data)
        if (data.channel?.alternatives?.[0]?.transcript) {
          const newTranscript = data.channel.alternatives[0].transcript
          
          if (data.is_final && newTranscript.trim()) {
            // 確定結果のみを追加（重複防止）
            confirmedTextRef.current += newTranscript + ' '
            setTranscript(confirmedTextRef.current)
            setLiveTranscript(confirmedTextRef.current)
          } else if (!data.is_final) {
            // 中間結果は確定テキスト + 現在の中間テキストを表示
            setLiveTranscript(confirmedTextRef.current + newTranscript)
          }
        }
      }

      socket.onerror = (error) => {
        console.error('Deepgram WebSocket error:', error)
        setError('Nova-3モデルに接続できません。Nova-2で再試行します。')
        stopRecording()
        setTimeout(() => startRecordingWithNova2(), 1000)
      }

      socket.onclose = () => {
        console.log('Deepgram WebSocket closed')
      }

    } catch (err) {
      console.error('Error starting recording:', err)
      setError('マイクへのアクセスが拒否されました')
    }
  }, [classifyWithGemini])

  // Nova-2フォールバック
  const startRecordingWithNova2 = useCallback(async () => {
    try {
      setError(null)
      
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: { channelCount: 1, sampleRate: 16000 } 
      })
      streamRef.current = stream

      const socket = new WebSocket(
        `wss://api.deepgram.com/v1/listen?model=nova-2&language=ja&punctuate=true&smart_format=true&interim_results=true`,
        ['token', DEEPGRAM_API_KEY]
      )
      socketRef.current = socket

      socket.onopen = () => {
        console.log('Deepgram WebSocket connected (Nova-2 fallback)')
        
        const mediaRecorder = new MediaRecorder(stream, {
          mimeType: 'audio/webm;codecs=opus',
        })
        mediaRecorderRef.current = mediaRecorder

        mediaRecorder.ondataavailable = (event) => {
          if (event.data.size > 0 && socket.readyState === WebSocket.OPEN) {
            socket.send(event.data)
          }
        }

        mediaRecorder.start(100)
        setIsRecording(true)

        processingIntervalRef.current = window.setInterval(() => {
          if (confirmedTextRef.current.trim()) {
            classifyWithGemini(confirmedTextRef.current)
          }
        }, 3000)
      }

      socket.onmessage = (event) => {
        const data = JSON.parse(event.data)
        if (data.channel?.alternatives?.[0]?.transcript) {
          const newTranscript = data.channel.alternatives[0].transcript
          
          if (data.is_final && newTranscript.trim()) {
            confirmedTextRef.current += newTranscript + ' '
            setTranscript(confirmedTextRef.current)
            setLiveTranscript(confirmedTextRef.current)
          } else if (!data.is_final) {
            setLiveTranscript(confirmedTextRef.current + newTranscript)
          }
        }
      }

      socket.onerror = () => {
        setError('音声認識の接続に失敗しました')
        stopRecording()
      }

    } catch (err) {
      setError('マイクへのアクセスが拒否されました')
    }
  }, [classifyWithGemini])

  const stopRecording = useCallback(() => {
    if (processingIntervalRef.current) {
      clearInterval(processingIntervalRef.current)
      processingIntervalRef.current = null
    }

    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop()
    }
    
    if (socketRef.current) {
      socketRef.current.close()
      socketRef.current = null
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop())
      streamRef.current = null
    }

    setIsRecording(false)

    // 最終分類を実行
    if (confirmedTextRef.current.trim()) {
      classifyWithGemini(confirmedTextRef.current)
    }
  }, [classifyWithGemini])

  const handleApplyTranscript = useCallback(() => {
    if (transcript.trim()) {
      onTranscript(transcript.trim())
    }
  }, [transcript, onTranscript])

  const handleClear = useCallback(() => {
    setTranscript('')
    setLiveTranscript('')
    setSoapPreview({ s: '', o: '', a: '', p: '' })
    confirmedTextRef.current = ''
    lastProcessedRef.current = ''
    setError(null)
  }, [])

  // 自動開始（初回マウント時のみ）
  useEffect(() => {
    if (autoStart && autoStartEnabled && !hasAutoStarted.current) {
      hasAutoStarted.current = true
      const timer = setTimeout(() => {
        startRecording()
      }, 500)
      return () => clearTimeout(timer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // 空の依存配列で初回のみ実行

  // コンポーネントのアンマウント時のみクリーンアップ
  useEffect(() => {
    const cleanup = () => {
      if (processingIntervalRef.current) {
        clearInterval(processingIntervalRef.current)
        processingIntervalRef.current = null
      }
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop()
      }
      if (socketRef.current) {
        socketRef.current.close()
        socketRef.current = null
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop())
        streamRef.current = null
      }
    }
    
    return cleanup
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // 空の依存配列でアンマウント時のみ実行

  return (
    <div className="voice-input">
      {/* 自動開始設定 */}
      <div className="voice-input__auto-start">
        <label className="voice-input__checkbox-label">
          <input
            type="checkbox"
            checked={autoStartEnabled}
            onChange={(e) => handleAutoStartChange(e.target.checked)}
          />
          <span>新規作成時に音声入力を自動開始</span>
        </label>
      </div>

      {/* Gemini APIキー入力 */}
      {showApiKeyInput && (
        <div className="voice-input__api-key">
          <label>Gemini APIキー（AI分類用）</label>
          <div className="voice-input__api-key-input">
            <input
              type="password"
              value={apiKeyInput}
              onChange={(e) => setApiKeyInput(e.target.value)}
              placeholder="AIzaSy..."
            />
            <button
              type="button"
              onClick={() => setShowApiKeyInput(false)}
              disabled={!apiKeyInput}
            >
              設定
            </button>
          </div>
          <p className="voice-input__api-key-hint">
            <a href="https://makersuite.google.com/app/apikey" target="_blank" rel="noopener noreferrer">
              Google AI Studioでキーを取得
            </a>
          </p>
        </div>
      )}

      <div className="voice-input__controls">
        {!isRecording ? (
          <button
            type="button"
            onClick={startRecording}
            className="voice-input__button voice-input__button--start"
            disabled={showApiKeyInput && !apiKeyInput}
          >
            <span className="material-symbols-outlined">mic</span>
            音声入力を開始
          </button>
        ) : (
          <button
            type="button"
            onClick={stopRecording}
            className="voice-input__button voice-input__button--stop"
          >
            <span className="material-symbols-outlined">stop</span>
            録音を停止
          </button>
        )}
        
        {isRecording && (
          <span className="voice-input__status">
            <span className="voice-input__pulse"></span>
            録音中... (Deepgram Nova-3)
          </span>
        )}

        {isProcessing && (
          <span className="voice-input__processing">
            <span className="voice-input__spinner"></span>
            AI分類中...
          </span>
        )}

        {!showApiKeyInput && apiKeyInput && (
          <button
            type="button"
            onClick={() => setShowApiKeyInput(true)}
            className="voice-input__settings"
            title="APIキー設定"
          >
            <span className="material-symbols-outlined">settings</span>
          </button>
        )}
      </div>

      {error && (
        <div className="voice-input__error">
          <span className="material-symbols-outlined">error</span>
          {error}
        </div>
      )}

      {/* リアルタイム文字起こし表示 */}
      {(liveTranscript || isRecording) && (
        <div className="voice-input__live">
          <label className="voice-input__label">
            <span className="material-symbols-outlined">subtitles</span>
            リアルタイム文字起こし
          </label>
          <div className="voice-input__live-text">
            {liveTranscript || '話し始めてください...'}
            {isRecording && <span className="voice-input__cursor">|</span>}
          </div>
        </div>
      )}

      {/* SOAP分類プレビュー */}
      {(soapPreview.s || soapPreview.o || soapPreview.a || soapPreview.p) && (
        <div className="voice-input__soap-preview">
          <label className="voice-input__label">
            <span className="material-symbols-outlined">auto_awesome</span>
            AI分類結果（リアルタイム）
          </label>
          <div className="soap-preview-grid">
            {soapPreview.s && (
              <div className="soap-preview-item soap-preview-item--s">
                <span className="soap-preview-badge">S</span>
                <p>{soapPreview.s}</p>
              </div>
            )}
            {soapPreview.o && (
              <div className="soap-preview-item soap-preview-item--o">
                <span className="soap-preview-badge">O</span>
                <p>{soapPreview.o}</p>
              </div>
            )}
            {soapPreview.a && (
              <div className="soap-preview-item soap-preview-item--a">
                <span className="soap-preview-badge">A</span>
                <p>{soapPreview.a}</p>
              </div>
            )}
            {soapPreview.p && (
              <div className="soap-preview-item soap-preview-item--p">
                <span className="soap-preview-badge">P</span>
                <p>{soapPreview.p}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {transcript && !isRecording && (
        <div className="voice-input__actions">
          <button
            type="button"
            onClick={handleClear}
            className="voice-input__action voice-input__action--secondary"
          >
            <span className="material-symbols-outlined">delete</span>
            クリア
          </button>
          <button
            type="button"
            onClick={handleApplyTranscript}
            className="voice-input__action voice-input__action--primary"
          >
            <span className="material-symbols-outlined">check</span>
            確定
          </button>
        </div>
      )}

      {!liveTranscript && !isRecording && !transcript && (
        <div className="voice-input__placeholder">
          <span className="material-symbols-outlined">record_voice_over</span>
          <p>{placeholder || '音声入力を開始すると、話した内容がリアルタイムで文字起こしされ、AIが自動的にSOAP形式に分類します'}</p>
        </div>
      )}

      <style>{`
        .voice-input {
          background-color: var(--color-solid-gray25);
          border: 2px dashed var(--border-color);
          border-radius: var(--border-radius-lg);
          padding: var(--spacing-lg);
        }

        .voice-input__auto-start {
          margin-bottom: var(--spacing-md);
          padding-bottom: var(--spacing-md);
          border-bottom: 1px solid var(--border-color);
        }

        .voice-input__checkbox-label {
          display: flex;
          align-items: center;
          gap: var(--spacing-sm);
          cursor: pointer;
          font-size: 0.875rem;
          color: var(--text-secondary);
        }

        .voice-input__checkbox-label input {
          width: 18px;
          height: 18px;
          accent-color: var(--color-blue500);
        }

        .voice-input__api-key {
          margin-bottom: var(--spacing-md);
          padding: var(--spacing-md);
          background-color: var(--color-blue50);
          border-radius: var(--border-radius-md);
        }

        .voice-input__api-key label {
          display: block;
          font-size: 0.75rem;
          font-weight: 500;
          color: var(--color-blue700);
          margin-bottom: var(--spacing-xs);
        }

        .voice-input__api-key-input {
          display: flex;
          gap: var(--spacing-sm);
        }

        .voice-input__api-key-input input {
          flex: 1;
          padding: var(--spacing-sm);
          border: 1px solid var(--color-blue200);
          border-radius: var(--border-radius-sm);
          font-size: 0.875rem;
        }

        .voice-input__api-key-input button {
          padding: var(--spacing-sm) var(--spacing-md);
          background-color: var(--color-blue500);
          color: white;
          border: none;
          border-radius: var(--border-radius-sm);
          font-size: 0.875rem;
          cursor: pointer;
        }

        .voice-input__api-key-input button:disabled {
          background-color: var(--color-solid-gray300);
          cursor: not-allowed;
        }

        .voice-input__api-key-hint {
          margin-top: var(--spacing-xs);
          font-size: 0.6875rem;
        }

        .voice-input__api-key-hint a {
          color: var(--color-blue600);
        }

        .voice-input__controls {
          display: flex;
          align-items: center;
          gap: var(--spacing-md);
          margin-bottom: var(--spacing-md);
          flex-wrap: wrap;
        }

        .voice-input__button {
          display: flex;
          align-items: center;
          gap: var(--spacing-sm);
          padding: var(--spacing-sm) var(--spacing-lg);
          border: none;
          border-radius: var(--border-radius-md);
          font-size: 0.875rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .voice-input__button--start {
          background-color: var(--color-blue500);
          color: white;
        }

        .voice-input__button--start:hover:not(:disabled) {
          background-color: var(--color-blue600);
        }

        .voice-input__button--start:disabled {
          background-color: var(--color-solid-gray300);
          cursor: not-allowed;
        }

        .voice-input__button--stop {
          background-color: var(--color-alert);
          color: white;
          animation: pulse 1.5s infinite;
        }

        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.8; }
        }

        .voice-input__status {
          display: flex;
          align-items: center;
          gap: var(--spacing-xs);
          font-size: 0.875rem;
          color: var(--color-alert);
        }

        .voice-input__pulse {
          width: 8px;
          height: 8px;
          background-color: var(--color-alert);
          border-radius: 50%;
          animation: blink 1s infinite;
        }

        @keyframes blink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }

        .voice-input__processing {
          display: flex;
          align-items: center;
          gap: var(--spacing-xs);
          font-size: 0.75rem;
          color: var(--color-blue600);
        }

        .voice-input__spinner {
          width: 14px;
          height: 14px;
          border: 2px solid var(--color-blue200);
          border-top-color: var(--color-blue600);
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .voice-input__settings {
          padding: var(--spacing-xs);
          background: none;
          border: none;
          color: var(--text-secondary);
          cursor: pointer;
          border-radius: var(--border-radius-sm);
        }

        .voice-input__settings:hover {
          background-color: var(--color-solid-gray100);
        }

        .voice-input__error {
          display: flex;
          align-items: center;
          gap: var(--spacing-sm);
          padding: var(--spacing-sm) var(--spacing-md);
          background-color: var(--color-alert-light);
          color: var(--color-alert);
          border-radius: var(--border-radius-md);
          font-size: 0.875rem;
          margin-bottom: var(--spacing-md);
        }

        .voice-input__live {
          background-color: white;
          border: 1px solid var(--border-color);
          border-radius: var(--border-radius-md);
          padding: var(--spacing-md);
          margin-bottom: var(--spacing-md);
        }

        .voice-input__label {
          display: flex;
          align-items: center;
          gap: var(--spacing-xs);
          font-size: 0.75rem;
          font-weight: 500;
          color: var(--text-secondary);
          margin-bottom: var(--spacing-sm);
        }

        .voice-input__label .material-symbols-outlined {
          font-size: 16px;
        }

        .voice-input__live-text {
          font-size: 0.875rem;
          line-height: 1.6;
          color: var(--text-primary);
          min-height: 60px;
          max-height: 150px;
          overflow-y: auto;
        }

        .voice-input__cursor {
          animation: cursorBlink 1s infinite;
          color: var(--color-blue500);
        }

        @keyframes cursorBlink {
          0%, 50% { opacity: 1; }
          51%, 100% { opacity: 0; }
        }

        .voice-input__soap-preview {
          background-color: white;
          border: 1px solid var(--color-blue200);
          border-radius: var(--border-radius-md);
          padding: var(--spacing-md);
          margin-bottom: var(--spacing-md);
        }

        .soap-preview-grid {
          display: flex;
          flex-direction: column;
          gap: var(--spacing-sm);
        }

        .soap-preview-item {
          display: flex;
          gap: var(--spacing-sm);
          padding: var(--spacing-sm);
          border-radius: var(--border-radius-sm);
          font-size: 0.8125rem;
        }

        .soap-preview-item--s { background-color: #e0f2fe; }
        .soap-preview-item--o { background-color: #d1fae5; }
        .soap-preview-item--a { background-color: #fef3c7; }
        .soap-preview-item--p { background-color: #ede9fe; }

        .soap-preview-badge {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          font-size: 0.6875rem;
          font-weight: 700;
          color: white;
          flex-shrink: 0;
        }

        .soap-preview-item--s .soap-preview-badge { background-color: #0891b2; }
        .soap-preview-item--o .soap-preview-badge { background-color: #059669; }
        .soap-preview-item--a .soap-preview-badge { background-color: #d97706; }
        .soap-preview-item--p .soap-preview-badge { background-color: #7c3aed; }

        .soap-preview-item p {
          flex: 1;
          margin: 0;
          line-height: 1.4;
        }

        .voice-input__actions {
          display: flex;
          justify-content: flex-end;
          gap: var(--spacing-sm);
        }

        .voice-input__action {
          display: flex;
          align-items: center;
          gap: var(--spacing-xs);
          padding: var(--spacing-xs) var(--spacing-md);
          border-radius: var(--border-radius-md);
          font-size: 0.75rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .voice-input__action--secondary {
          background-color: transparent;
          border: 1px solid var(--border-color);
          color: var(--text-secondary);
        }

        .voice-input__action--secondary:hover {
          background-color: var(--color-solid-gray50);
        }

        .voice-input__action--primary {
          background-color: var(--color-blue500);
          border: none;
          color: white;
        }

        .voice-input__action--primary:hover {
          background-color: var(--color-blue600);
        }

        .voice-input__action .material-symbols-outlined {
          font-size: 16px;
        }

        .voice-input__placeholder {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: var(--spacing-lg);
          color: var(--text-secondary);
          text-align: center;
        }

        .voice-input__placeholder .material-symbols-outlined {
          font-size: 48px;
          margin-bottom: var(--spacing-sm);
          opacity: 0.5;
        }

        .voice-input__placeholder p {
          font-size: 0.875rem;
          max-width: 400px;
        }
      `}</style>
    </div>
  )
}
